// useCreateWorkspace.ts — creación del workspace al final del onboarding.
// Qué hace: crea el workspace (create-from-scan o manual), sube el logo file si
//   hay uno pendiente (isLogo=true), reporta el evento y maneja el step done/error.
// Qué exporta: useCreateWorkspace (devuelve { handleCreate }).
// Quién lo usa: CreateWorkspaceModal (y goToTemplates/content-mix que llaman handleCreate).

import { Workspace, BrandAsset } from '@agency-smm/shared';
import { apiClient } from '../../services/apiClient';
import { reportEvent } from '../../utils/coopenEvents';
import { removeWhiteBackground, blobToLogoFile } from '../../utils/removeLogoBackground';
import { WizardStep, ScanResult, ContentMix } from '../../components/createWorkspace/types';

interface Deps {
  scanResult: ScanResult | null;
  brandName: string;
  manualName: string;
  selectedLogo: string | null;
  logoFile: File | null;
  /** Variantes de logo IA no elegidas → se guardan como brand assets (isLogo=false). */
  extraLogoUrls: string[];
  selectedPhotos: Set<string>;
  selectedTemplates: Set<string>;
  selectedFont: string | null;
  contentMix: ContentMix;
  setStep: React.Dispatch<React.SetStateAction<WizardStep>>;
  setIsCreating: (v: boolean) => void;
  setCreatedWs: (ws: Workspace) => void;
  addMsg: (msg: { from: 'novah' | 'user'; text: string; widget?: 'success' }) => void;
}

export function useCreateWorkspace(deps: Deps) {
  const {
    scanResult, brandName, manualName, selectedLogo, logoFile, extraLogoUrls, selectedPhotos,
    selectedTemplates, selectedFont, contentMix, setStep, setIsCreating,
    setCreatedWs, addMsg,
  } = deps;

  const handleCreate = async () => {
    // Content mix defaults to balanced 25/25/25/25 — user can override at generation time
    setStep('creating');
    setIsCreating(true);

    setTimeout(() => addMsg({ from: 'novah', text: 'Creating your workspace...' }), 200);

    try {
      let workspace: Workspace;
      // Determine if logo is a real URL or a blob (file upload pending)
      const logoIsFile = logoFile && selectedLogo?.startsWith('blob:');
      const logoUrlForServer = logoIsFile ? undefined : (selectedLogo || undefined);

      if (scanResult) {
        workspace = await apiClient.post<Workspace>('/api/workspaces/create-from-scan', {
          name: brandName.trim(),
          url: scanResult.url,
          logoUrl: logoUrlForServer,
          backgroundImageUrls: [...selectedPhotos],
          brandInfo: scanResult.brandInfo,
          contentMix,
          selectedTemplateIds: [...selectedTemplates],
          brandColors: scanResult.brandColors || [],
          brandFonts: selectedFont ? [selectedFont] : (scanResult.brandFonts || []),
        });
      } else {
        workspace = await apiClient.post<Workspace>('/api/workspaces', { name: manualName.trim() });
      }

      // Upload logo file if provided (from logo-alt or manual flow)
      if (logoFile) {
        try {
          const fd = new FormData();
          fd.append('file', logoFile);
          fd.append('isLogo', 'true');
          await apiClient.upload<BrandAsset>(`/api/workspaces/${workspace.id}/assets/upload`, fd);
        } catch { /* logo upload failed, non-fatal */ }
      }

      // Las otras variantes de logo IA (no elegidas) se guardan como brand assets
      // (isLogo=false) — quedan disponibles para el picker "+ Logo". No-fatal.
      for (const url of extraLogoUrls) {
        try {
          const blob = await removeWhiteBackground(url);
          const file = blobToLogoFile(blob, brandName || manualName || 'logo');
          const fd = new FormData();
          fd.append('file', file);
          fd.append('isLogo', 'false');
          await apiClient.upload<BrandAsset>(`/api/workspaces/${workspace.id}/assets/upload`, fd);
        } catch { /* non-fatal */ }
      }

      // Global templates are referenced via selectedTemplateIds in the workspace record.
      // No need to clone — they show up in the template list automatically.
      // Users can clone manually from the templates page if they want a workspace copy.

      setCreatedWs(workspace);
      reportEvent('workspace.created', {
        workspaceId: workspace.id,
        method: scanResult ? 'scan' : 'manual',
        nota: scanResult
          ? `Workspace creado a partir de scan de ${scanResult.url}`
          : 'Workspace creado manualmente',
      });
      setTimeout(() => {
        const fontMsg = selectedFont ? ` Using **${selectedFont}** as your brand font.` : '';
        addMsg({ from: 'novah', text: `Your workspace **${workspace.name}** is ready!${fontMsg} Let's go.`, widget: 'success' });
        setStep('done');
        setIsCreating(false);
      }, 500);
    } catch (err) {
      addMsg({ from: 'novah', text: `Failed to create workspace: ${err instanceof Error ? err.message : 'Unknown error'}` });
      reportEvent('workspace.create_failed', {
        method: scanResult ? 'scan' : 'manual',
        nota: `Falló creación de workspace: ${err instanceof Error ? err.message : 'unknown'}`,
      });
      setIsCreating(false);
      setStep('content-mix');
    }
  };

  return { handleCreate };
}
